import Tag from "@/components/Tag";
import { TagType } from "@/components/Tag/Tag";
import type { TagExtras } from "@/components/Table/buildTagColumn";
import type { MaterialNames } from "@/components/material_symbol_names";
import { toDateFormat } from "@/lib/dateHelpers";
import { capitalize } from "lodash";
import { DateTime } from "luxon";
import { OrderPaymentStatus } from "@/pages/billing/types";
import { OrderStatus } from "./[eventSlug]/types";
import type { Session } from "./types";

export const EVENT_CODE_SEARCH_SUPPORT_KEY = "m360.events.eventCodeSearchSupported.v2";

/** Returns true only when a prior introspection confirmed API support. */
export function readCachedEventCodeSearchSupport(): boolean {
  return sessionStorage.getItem(EVENT_CODE_SEARCH_SUPPORT_KEY) === "true";
}

export function shouldResetEventsListPagination({
  searchChanged,
  orderByChanged,
  supportChanged,
  debouncedSearch
}: {
  searchChanged: boolean;
  orderByChanged: boolean;
  supportChanged: boolean;
  debouncedSearch: string;
}): boolean {
  return searchChanged || orderByChanged || (supportChanged && Boolean(debouncedSearch));
}

/** Persist positive results only so a pre-deploy visit does not disable search for the tab session. */
export function writeCachedEventCodeSearchSupport(supported: boolean): void {
  if (supported) {
    sessionStorage.setItem(EVENT_CODE_SEARCH_SUPPORT_KEY, "true");
  } else {
    sessionStorage.removeItem(EVENT_CODE_SEARCH_SUPPORT_KEY);
  }
}

function earliestIsoTimestamp(timestamps: string[]): string | undefined {
  let earliest: DateTime | undefined;
  let earliestIso: string | undefined;

  for (const timestamp of timestamps) {
    const parsed = DateTime.fromISO(timestamp, { setZone: true });
    if (!parsed.isValid) {
      continue;
    }
    if (!earliest || parsed < earliest) {
      earliest = parsed;
      earliestIso = timestamp;
    }
  }

  return earliestIso;
}

function latestIsoTimestamp(timestamps: string[]): string | undefined {
  let latest: DateTime | undefined;
  let latestIso: string | undefined;

  for (const timestamp of timestamps) {
    const parsed = DateTime.fromISO(timestamp, { setZone: true });
    if (!parsed.isValid) {
      continue;
    }
    if (!latest || parsed > latest) {
      latest = parsed;
      latestIso = timestamp;
    }
  }

  return latestIso;
}

type EventListDateSource = {
  startsAt: string;
  endsAt: string;
  timezone?: string | null;
  sessions?: Session[] | null;
};

export function getEventListStartsAt(event: EventListDateSource): string {
  const sessionStarts = event.sessions?.map((session) => session.startsAt).filter(Boolean) ?? [];
  if (sessionStarts.length === 0) {
    return event.startsAt;
  }
  return earliestIsoTimestamp(sessionStarts) ?? event.startsAt;
}

export function getEventListEndsAt(event: EventListDateSource): string {
  const sessionEnds = event.sessions?.map((session) => session.endsAt).filter(Boolean) ?? [];
  if (sessionEnds.length === 0) {
    return event.endsAt;
  }
  return latestIsoTimestamp(sessionEnds) ?? event.endsAt;
}

export function formatEventListDate(isoDate: string, timezone?: string | null): string {
  return toDateFormat(isoDate, timezone ? { tz: timezone } : {}) || "";
}

type EventRegistrationLimitSource = {
  registrationCount: number;
  sessions?: Pick<Session, "maxAttendance">[] | null;
};

export function getEventRegistrationLimit({ sessions }: Pick<EventRegistrationLimitSource, "sessions">): number | null {
  if (!sessions?.length) {
    return null;
  }

  const [firstSession] = sessions;
  const maxAttendance = firstSession.maxAttendance;

  if (typeof maxAttendance !== "number" || !Number.isFinite(maxAttendance) || maxAttendance <= 0) {
    return null;
  }

  const allSessionsShareLimit = sessions.every((session) => session.maxAttendance === maxAttendance);

  return allSessionsShareLimit ? maxAttendance : null;
}

export function formatEventRegistrations(event: EventRegistrationLimitSource): string {
  const registrationLimit = getEventRegistrationLimit(event);

  if (!registrationLimit) {
    return String(event.registrationCount);
  }

  return `${event.registrationCount} / ${registrationLimit}`;
}

type BillingStatusConfig = { color: TagType; label: string; icon?: MaterialNames };

const BILLING_STATUS_MAP: Record<string, BillingStatusConfig> = {
  [OrderStatus.PAID]: { color: "success", label: "Paid" },
  [OrderStatus.OPEN]: { color: "warn", label: "Open" },
  [OrderStatus.CANCELED]: { color: "surface", label: "Canceled" },
  [OrderStatus.PENDING]: { color: "warn", label: "Pending" },
  [OrderStatus.FAILED]: { color: "error", label: "Failed", icon: "error" }
};

export type OrderStatusTagSource = {
  orderStatus?: OrderStatus | string | null;
  amountRefunded?: string;
  isCanceled?: boolean;
  paymentStatus?: OrderPaymentStatus | string | null;
};

function hasOrderRefund({ orderStatus, amountRefunded, isCanceled, paymentStatus }: OrderStatusTagSource): boolean {
  return Boolean(
    isCanceled &&
    amountRefunded &&
    amountRefunded !== "$0.00" &&
    (orderStatus === OrderStatus.CANCELED || paymentStatus === OrderPaymentStatus.REFUNDED)
  );
}

export function getOrderStatusTagLabel(orderStatus?: OrderStatus | string | null): string | null {
  if (!orderStatus || !BILLING_STATUS_MAP[orderStatus]) {
    return null;
  }
  return BILLING_STATUS_MAP[orderStatus].label;
}

export function getOrderStatusTagType(orderStatus?: OrderStatus | string | null): TagType {
  if (!orderStatus || !BILLING_STATUS_MAP[orderStatus]) {
    return "surface";
  }
  return BILLING_STATUS_MAP[orderStatus].color;
}

export function getOrderStatusTagExtras(source: OrderStatusTagSource): TagExtras {
  if (hasOrderRefund(source)) {
    return {
      iconName: "money_off",
      tooltipText: "Order has been canceled and payment was refunded"
    };
  }

  const iconName = source.orderStatus ? BILLING_STATUS_MAP[source.orderStatus]?.icon : undefined;
  return iconName ? { iconName } : {};
}

export const renderBillingStatus = (stage: OrderStatus | string, tagSize: "small" | "medium" = "medium") => {
  const label = getOrderStatusTagLabel(stage);
  if (!label) {
    return <span className="opacity-20">--</span>;
  }

  const { iconName } = getOrderStatusTagExtras({ orderStatus: stage });

  return (
    <Tag tagType={getOrderStatusTagType(stage)} testId={label} iconName={iconName} size={tagSize}>
      {label}
    </Tag>
  );
};

type EventStatusConfig = { color: TagType; label: string; testId: string; icon?: MaterialNames };

const EVENT_STATUS_MAP: Record<string, EventStatusConfig> = {
  Active: { color: "success", label: "Active", testId: "active" },
  Pending: { color: "warn", label: "Pending", testId: "pending" },
  Draft: { color: "information", label: "Draft", testId: "draft" },
  Canceled: { color: "surface", label: "Canceled", testId: "cancelled", icon: "event_busy" },
  Completed: { color: "surface", label: "Completed", testId: "completed" }
};

export const renderEventStatus = (status: string) => {
  const data = EVENT_STATUS_MAP[status] || {
    color: "tertiary" as TagType,
    label: capitalize(status),
    testId: status.toLowerCase()
  };

  return (
    <Tag tagType={data.color} size="small" testId={data.testId} iconName={data.icon}>
      {data.label}
    </Tag>
  );
};
